import { Routes } from '@angular/router';
import { PageNotFoundComponent } from './core/components/page-not-found/page-not-found.component';
import { HomeComponent } from './core/components/home/home.component';
import { authGuard } from './auth/auth.guard';
import { withUnsavedChangesGuard } from './guards/pendingchanges.guard';
import { hasRolesGuard } from './guards/has-roles.guard';
import { requirePermission } from './guards/permission.guard';
import { Permissions } from './services/user-permission.service';

export const routes: Routes = withUnsavedChangesGuard([
  {
    path: 'login',
    component: HomeComponent,
  },
  {
    path: 'login-failed',
    component: HomeComponent
  },
  {
    // Standalone page for users with no roles or blocked by requirePermission().
    path: 'not-authorized',
    loadComponent: () =>
      import('./features/not-authorized/not-authorized.component').then(
        (m) => m.NotAuthorizedComponent
      ),
    canActivate: [authGuard],
    data: { hideShell: true },
    title: 'Not Authorized',
  },
  // Send the bare URL to the dashboard. This MUST stay before the '' children route below, otherwise
  // that route matches the empty path first and its own wildcard renders Page Not Found.
  { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
  {
    // Every child route requires the user to have at least one role.
    path: '',
    canActivateChild: [hasRolesGuard],
    children: [
      {
        path: 'regions',
        loadChildren: () =>
          import('./features/region/region-routing.module').then(
            (m) => m.RegionRoutingModule
          ),
        canActivate: [
          authGuard,
          requirePermission([
            Permissions.RegionsView,
            Permissions.RegionsEdit,
          ]),
        ],
        title: 'Regions',
      },
      {
        path: 'divisions',
        loadChildren: () =>
          import('./features/division/division-routing.module').then(
            (m) => m.DivisionRoutingModule
          ),
        canActivate: [
          authGuard,
          requirePermission([
            Permissions.DivisionsView,
            Permissions.DivisionsEdit,
          ]),
        ],
        title: 'Brands',
      },
      {
        path: 'vehicle/create',
        loadComponent: () =>
          import(
            './features/vehicle-maintenance/vehicle-details/vehicle-details.component'
          ).then((m) => m.VehicleDetailsComponent),
        // Creating a vehicle is an edit action, so it requires edit (not just view).
        canActivate: [authGuard, requirePermission(Permissions.VehiclesEdit)],
        title: 'Create Vehicle',
      },
      {
        path: 'maintenance',
        loadChildren: () =>
          import(
            './features/vehicle-maintenance/maintenance-routing.module'
          ).then((m) => m.MaintenanceRoutingModule),
        canActivate: [
          authGuard,
          requirePermission([
            Permissions.VehiclesView,
            Permissions.VehiclesEdit,
          ]),
        ],
        title: 'Vehicle Maintenance',
      },
      {
        path: 'data-updates',
        loadChildren: () =>
          import('./features/data-updates/data-updates-routing.module').then(
            (m) => m.DataUpdatesRoutingModule
          ),
        canActivate: [
          authGuard,
          requirePermission([
            Permissions.DataMaintenanceView,
            Permissions.DataMaintenanceEdit,
          ]),
        ],
        title: 'Data Update Maintenance',
      },
      {
        path: 'data-exports',
        loadChildren: () =>
          import('./features/data-exports/data-exports-routing.module').then(
            (m) => m.DataExportsRoutingModule
          ),
        canActivate: [
          authGuard,
          // Any Data Exports access — the "all reports" view, import, or any single-report view.
          requirePermission([
            Permissions.DataExportsView,
            Permissions.DataExportsImport,
            Permissions.DataExportsDistributionUpdatesView,
            Permissions.DataExportsTranslationBatchView,
            Permissions.DataExportsCfdVehicleReportsView,
            Permissions.DataExportsRpoVehicleReportsView,
          ]),
        ],
        title: 'Data Exports',
      },
      {
        path: 'rpos',
        loadComponent: () =>
          import('./features/rpo/rpo-list.component').then(
            (m) => m.RpoListComponent
          ),
        canActivate: [
          authGuard,
          requirePermission([Permissions.RposView, Permissions.RposEdit]),
        ],
        title: 'RPO Maintenance',
      },
      {
        path: 'cfds',
        loadComponent: () =>
          import('./features/cfd/cfd-list.component').then(
            (m) => m.CfdListComponent
          ),
        canActivate: [
          authGuard,
          requirePermission([Permissions.CfdsView, Permissions.CfdsEdit]),
        ],
        title: 'CFD Maintenance',
      },
      {
        path: 'uploads',
        loadComponent: () =>
          import(
            './features/uploads/uploads-page/uploads-page.component'
          ).then((m) => m.UploadsPageComponent),
        canActivate: [
          authGuard,
          requirePermission([Permissions.FilesView, Permissions.FilesEdit]),
        ],
        title: 'Uploads',
      },
      {
        path: 'history',
        loadChildren: () =>
          import('./features/user-history/user-history-routes').then(
            (m) => m.USER_HISTORY_ROUTES
          ),
        canActivate: [authGuard],
        title: 'User History',
      },
      {
        path: 'users',
        loadChildren: () =>
          import('./features/manage-users/manage-users-routes').then(
            (m) => m.MANAGE_USERS_ROUTES
          ),
        canActivate: [
          authGuard,
          requirePermission(Permissions.UsersEdit),
        ],
      },
      {
        path: 'roles',
        loadComponent: () =>
          import('./features/manage-roles/manage-roles.component').then(
            (m) => m.ManageRolesComponent
          ),
        canActivate: [
          authGuard,
          requirePermission(Permissions.RolesEdit),
        ],
        title: 'Manage Roles',
      },
      {
        path: 'system',
        loadChildren: () =>
          import('./features/system/system-routing.module').then(
            (m) => m.SystemRoutingModule
          ),
        canActivate: [
          authGuard,
          requirePermission(Permissions.SystemView),
        ],
        title: 'System',
      },
      {
        path: 'dashboard',
        component: HomeComponent,
        canActivate: [authGuard],
        title: 'My Dashboard'
      },
      {
        path: 'calendar',
        loadComponent: () =>
          import('./features/calendar/calendar.component').then(
            (m) => m.CalendarComponent
          ),
        canActivate: [authGuard],
        title: 'Calendar'
      },
      {
        path: 'checklist',
        loadComponent: () =>
          import('./features/vehicle-checklist/vehicle-checklist.component').then(
            (m) => m.VehicleChecklistComponent
          ),
        canActivate: [authGuard],
        title: 'Vehicle Checklist'
      },
      {
        path: '**',
        component: PageNotFoundComponent,
        title: 'Page Not Found',
      },
    ],
  },
  { path: '**', component: PageNotFoundComponent, title: 'Page Not Found' },
]);

